import { API_URL, getHeaders } from "../Config";

/**
 * Deduplicación simple: si llega la misma request (misma key) mientras está en vuelo,
 * se reutiliza la promesa. Se libera la key 500ms después de terminar.
 */
const pendingRequests = new Map();

const deduplicate = (key, fetcher) => {
  if (pendingRequests.has(key)) return pendingRequests.get(key);

  const promise = fetcher().finally(() => {
    setTimeout(() => pendingRequests.delete(key), 500);
  });

  pendingRequests.set(key, promise);
  return promise;
};

const request = async (
  url,
  { method = "GET", headers, body, credentials, redirect } = {},
) => {
  const response = await fetch(url, {
    method,
    headers,
    body,
    credentials,
    redirect,
  });

  if (!response.ok) throw new Error(`Error: ${response.statusText}`);
  return response.json();
};

const buildKey = (name, payload) => `${name}-${JSON.stringify(payload)}`;

/* =========================
   COTIZACIONES / CLIENTE
========================= */
export const getCotizacionesPropuestas = (codigo) => {
  const key = `getCotizacionesPropuestas-${codigo}`;
  return deduplicate(key, async () => {
    const data = await request(
      `${API_URL}cotizacion/propuestas?codigo=${codigo}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    );
    return data;
  });
};

export const getCotizacionesTabla = ({
  tipo,
  rut,
  codigo = "",
  razonSocial = "",
  limit = 50,
  order = "",
}) => {
  const key = buildKey("getCotizacionesTabla", {
    tipo,
    rut,
    codigo,
    razonSocial,
    limit,
    order,
  });

  return deduplicate(key, async () => {
    const params = new URLSearchParams({
      tipo,
      rut,
      codigo,
      razonSocial,
      limit: String(limit),
      order,
    });

    const endpoint = `${API_URL}cotizacion/tabla?${params.toString()}`;

    const data = await request(endpoint, {
      method: "GET",
      credentials: "include",
      redirect: "follow",
      headers: getHeaders(),
    });

    if (!data || !data.clientes)
      throw new Error("Datos inválidos recibidos del servidor.");
    return data.clientes;
  });
};

export const getDatosCliente = (codigo) => {
  const key = `getDatosCliente-${codigo}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/datos?codigo=${codigo}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getInfoCliente = (codigo) => {
  const key = `getInfoCliente-${codigo}`;
  return deduplicate(key, () =>
    request(`${API_URL}cliente/info?codigo=${codigo}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getProductosMasVendidos = (codigo, cantidad = 10) => {
  const key = `getProductosMasVendidos-${codigo}-${cantidad}`;
  return deduplicate(key, async () => {
    const data = await request(
      `${API_URL}cotizacion/productosMasVendidos?codigo=${codigo}&cantidad=${cantidad}`,
      { method: "GET", headers: getHeaders() },
    );
    return data?.masVendidos || [];
  });
};

export const getSincronizarCliente = (codigo, index) => {
  const key = `getSincronizarCliente-${codigo}-${index}`;
  return deduplicate(key, async () => {
    const data = await request(
      `${API_URL}cliente/codigo?codigo=${codigo}&index=${index}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    );
    return data?.cliente || {};
  });
};

export const getCotizacion = (codigo) => {
  const key = `getCotizacion-${codigo}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/cotizacion?codigo=${codigo}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getStock = (articulo) => {
  const key = `getStock-${articulo}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/stock?articulo=${articulo}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

/* =========================
    GRABAR / PUBLICAR
========================= */

export const postGrabar = (data) => {
  const key = "grabar";
  return deduplicate(key, async () => {
    const endpoint = `${API_URL}cotizacion/grabar`;

    const headers = getHeaders();
    headers.delete("Content-Type");

    const formData = new URLSearchParams();
    Object.entries(data).forEach(([k, v]) => {
      if (v !== undefined && v !== null) formData.append(k, v);
    });

    return request(endpoint, {
      method: "POST",
      headers,
      body: formData,
    });
  });
};

export const postGrabarClienteBoleta = (nombre, telefono, correo) => {
  const key = `grabar-${nombre}-${telefono}-${correo}`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/clienteBoleta/grabar`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ nombre, telefono, correo }),
    }),
  );
};

// Nota: esto estaba "nok" porque fetch NO usa "params". Se corrige usando querystring.
export const getClienteBoleta = (id) => {
  const key = `getClienteBoleta-${id}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}mantenedor/clienteBoleta/input?id=${encodeURIComponent(id)}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const publicarCliente = (cliente) => {
  const key = `publicarCliente-${JSON.stringify(cliente)}`;
  return deduplicate(key, async () => {
    const endpoint = `${API_URL}cliente/publicar`;

    const headers = getHeaders();
    headers.delete("Content-Type");
    headers.append("Content-Type", "application/x-www-form-urlencoded");

    const body = new URLSearchParams(cliente).toString();

    return request(endpoint, { method: "POST", headers, body });
  });
};

export const grabarClienteBoleta = (cliente) => {
  const key = `grabarClienteBoleta-${JSON.stringify(cliente)}`;
  return deduplicate(key, async () => {
    const endpoint = `${API_URL}mantenedor/clienteBoleta/grabar`;

    const headers = getHeaders();
    headers.delete("Content-Type");
    headers.append("Content-Type", "application/x-www-form-urlencoded");

    const body = new URLSearchParams(cliente).toString();

    return request(endpoint, { method: "POST", headers, body });
  });
};

/* =========================
    COTIZACION: COMBOS / DETALLE / RESUMEN / RELACIONADOS
========================= */

// Nota: lo dejaste marcado como "nok", pero esto funciona bien.
export const getComboCierre = (id_cotizacion, especial = false) => {
  const key = `getComboCierre-${id_cotizacion}-${especial}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/comboCierre?id_cotizacion=${id_cotizacion}&especial=${especial}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const getdetalle = (dialog, especial, id_cotizacion, interfaz) => {
  const key = `getdetalle-${dialog}-${especial}-${id_cotizacion}-${interfaz}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/detalle?dialog=${dialog}&especial=${especial}&id_cotizacion=${id_cotizacion}&interfaz=${interfaz}`,
      { method: "GET", headers: getHeaders() },
    ),
  );
};

export const getResumen = (id, descuento = 0, stock = false) => {
  const descuentoSeguro =
    descuento === undefined ||
    descuento === null ||
    descuento === "" ||
    descuento === "undefined"
      ? 0
      : descuento;

  const key = `getResumen-${id}-${descuentoSeguro}-${stock}`;

  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/resumen?id=${id}&descuento=${descuentoSeguro}&stock=${stock}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const getRelacionados = (id_cotizacion) => {
  const key = `getRelacionados-${id_cotizacion}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/relacionados?id_cotizacion=${id_cotizacion}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

/* =========================
   PRODUCTOS / PRECIOS
========================= */

export const getProductos = ({ descripcion, start = 1, length = 20 }) => {
  const key = `getProductos-${start}-${length}-${descripcion}`;
  return deduplicate(key, () => {
    const params = new URLSearchParams({
      descripcion,
      start: String(start),
      length: String(length),
    });
    return request(`${API_URL}productos?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(),
    });
  });
};

export const getPrecio = (codigo, listaPrecio) => {
  const key = `getPrecio-${codigo}-${listaPrecio}`;
  return deduplicate(key, () =>
    request(`${API_URL}precio?codigo=${codigo}&listaPrecio=${listaPrecio}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getPrecios = ({
  dialog = false,
  draw = 1,
  id,
  search = "",
  length = 20,
  old = false,
  start = 1,
  tipo,
}) => {
  const key = `getPrecios-${dialog}-${draw}-${id}-${start}-${length}-${old}-${tipo}-${search}`;
  return deduplicate(key, () => {
    const params = new URLSearchParams({
      dialog: String(dialog),
      draw: String(draw),
      id: String(id),
      "search[value]": search,
      length: String(length),
      old: String(old),
      start: String(start),
      tipo: String(tipo),
    });

    return request(`${API_URL}precios?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(),
    });
  });
};

export const getNuevo = () => {
  const key = "getNuevo";
  return deduplicate(key, () =>
    request(`${API_URL}cliente/nuevo`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getClienteRescatar = (rut) => {
  const key = `getClienteRescatar-${rut}`;
  return deduplicate(key, () =>
    request(`${API_URL}cliente/rescatar?rut=${rut}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

/* =========================
   ACTUALIZACIONES / ACCIONES DE COTIZACION
========================= */

export const eliminardetalleCotizacion = (id) => {
  const key = `eliminarCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/eliminar?id=${id}`, {
      method: "DELETE",
      headers: getHeaders(),
    }),
  );
};

export const actualizarProyecto = (id_cotizacion, id_proyecto) => {
  const key = `actualizarProyecto-${id_cotizacion}-${id_proyecto}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/actualiza/proyecto?id_cotizacion=${id_cotizacion}&id_proyecto=${id_proyecto}`,
      { method: "GET", headers: getHeaders() },
    ),
  );
};

export const agregarProducto = (id_cotizacion, codigo) => {
  const key = `agregarProducto-${id_cotizacion}-${codigo}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/agregar/producto?codigo=${codigo}&id_cotizacion=${id_cotizacion}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

// Nota: esto duplica endpoint con eliminarDetalle. Lo dejé tal cual para no romper llamados.
export const eliminarProducto = (id) => {
  const key = `eliminarProducto-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/eliminar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const actualizarCantidad = (cantidad, id) => {
  const key = `actualizarCantidad-${cantidad}-${id}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/actualiza/cantidad?cantidad=${cantidad}&id=${id}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const actualizarPrecios = (id) => {
  const key = `actualizarPrecios-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/actualizar/precios?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const descartarCotizacion = (id) => {
  const key = `descartarCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/descartar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const descartarCotizacionBloqueada = (id) => {
  const key = `descartarCotizacionBloqueada-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/descartar/bloqueada?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const actualizarDescuento = (descuento, id) => {
  const key = `actualizarDescuento-${descuento}-${id}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/actualiza/descuento?descuento=${descuento}&id=${id}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const notificarCotizacion = (id_cotizacion) => {
  const key = `notificarCotizacion-${id_cotizacion}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/notificar?id_cotizacion=${id_cotizacion}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const existenciaCotizacion = (id_cotizacion) => {
  const key = `existenciaCotizacion-${id_cotizacion}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/existencia?id_cotizacion=${id_cotizacion}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const clienteBoletaGrabar = (correo, nombre, telefono) => {
  const key = `clienteBoletaGrabar-${correo}-${nombre}-${telefono}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}clienteBoleta/grabar?correo=${correo}&nombre=${nombre}&telefono=${telefono}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const actualizarObservaciones = (id, observaciones) => {
  const key = `actualizarObservaciones-${id}-${observaciones}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/actualiza/observaciones?id=${id}&observaciones=${encodeURIComponent(
        observaciones ?? "",
      )}`,
      { method: "GET", headers: getHeaders() },
    ),
  );
};

export const getCondicionesEntrega = () => {
  const key = "getCondicionesEntrega";

  return deduplicate(key, async () => {
    const data = await request(`${API_URL}cotizacion/condiciones-entrega`, {
      method: "GET",
      headers: getHeaders(),
    });

    return Array.isArray(data?.condiciones) ? data.condiciones : [];
  });
};

export const actualizarEntrega = (id, condicion) => {
  const key = `actualizarEntrega-${id}-${condicion}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/entrega?condicion=${condicion}&cotizacion=${id}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const actualizarDespacho = (
  id,
  cambia,
  comuna = null,
  direccion = null,
  referencia = null,
) => {
  const key = `actualizarDespacho-${id}-${cambia}-${comuna ?? "null"}-${
    direccion ?? "null"
  }-${referencia ?? "null"}`;

  return deduplicate(key, () => {
    const params = new URLSearchParams({
      cambia,
      comuna: comuna ?? "",
      cotizacion: String(id),
      direccion: direccion ?? "",
      referencia: referencia ?? "",
    });

    return request(`${API_URL}cotizacion/despacho?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(),
    });
  });
};

export const validarCotizacion = (id) => {
  const key = `validarCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/validar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const finalizarCotizacion = (id, descuento = 0, observaciones) => {
  const descuentoSeguro = Number.isFinite(Number(descuento))
    ? Number(descuento)
    : 0;

  const key = `finalizarCotizacion-${id}-${descuentoSeguro}-${observaciones}`;

  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/finalizar?id=${id}&descuento=${descuentoSeguro}&observaciones=${encodeURIComponent(
        observaciones ?? "",
      )}`,
      { method: "GET", headers: getHeaders() },
    ),
  );
};

export const abrirCotizacionPDF = async (id) => {
  const endpoint = `${API_URL}reporte/cotizaciones/pdf?historia=false&id=${id}`;
  const pdfWindow = window.open("", "_blank");

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`No fue posible generar el PDF (${response.status})`);
    }

    const blob = await response.blob();
    const pdfUrl = window.URL.createObjectURL(
      blob.type === "application/pdf"
        ? blob
        : new Blob([blob], { type: "application/pdf" }),
    );

    if (pdfWindow) {
      pdfWindow.location.href = pdfUrl;
    } else {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    }

    window.setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 60000);
  } catch (error) {
    if (pdfWindow) {
      pdfWindow.close();
    }
    console.error("Error abriendo PDF de cotización:", error);
    throw error;
  }
};

export const inyectarCotizacion = (id) => {
  const key = `inyectarCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/inyectar?forzar=false&id=${id}&test=false`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const abrirValePDF = (id) => {
  const endpoint = `${API_URL}reporte/cotizaciones/vale?historia=false&id=${id}`;
  window.open(endpoint, "_blank", "noopener,noreferrer");
};

/* =========================
   CONTACTOS
========================= */

export const grabarContacto = (codigo, email, id, nombre, telefono) => {
  const key = `grabarContacto-${codigo}-${email}-${id}-${nombre}-${telefono}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}contactos/grabar?codigo=${codigo}&email=${email}&id=${id}&nombre=${nombre}&telefono=${telefono}`,
      { method: "GET", headers: getHeaders() },
    ),
  );
};

export const getContactosCliente = (codigo) => {
  const key = `getContactosCliente-${codigo}`;
  return deduplicate(key, () =>
    request(`${API_URL}contactos/cliente?codigo=${codigo}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

/* =========================
AJUSTES ADICIONALES
========================= */

export const actualizarPrecio = (id, precio) => {
  const key = `actualizarPrecio-${id}-${precio}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/actualiza/precio?dialog=false&id=${id}&precio=${precio}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const actualizarFlete = (id, valor) => {
  const key = `actualizarFlete-${id}-${valor}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/flete?id=${id}&valor=${valor}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

/* =========================
LOGIN
========================= */
export const loginService = (username, password) => {
  const key = `login-${username}-${password}`;
  return deduplicate(key, () =>
    request(`${API_URL}login?password=${password}&username=${username}`, {
      method: "POST",
    }),
  );
};

export const getAlmacen = (id, almacen) => {
  const key = `getAlmacen-${id}-${almacen}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}cotizacion/almacen?almacen=${encodeURIComponent(
        almacen ?? "",
      )}&id=${id}`,
      { method: "GET", headers: getHeaders() },
    ),
  );
};

export const getUsuario = () => {
  const key = `getUsuario`;
  return deduplicate(key, () =>
    request(`${API_URL}user`, { method: "GET", headers: getHeaders() }),
  );
};

/* =========================
reporte cotizacion
========================= */
export const getFiltrosReporteCotizacion = () => {
  const key = `getFiltrosReporteCotizacion`;
  return deduplicate(key, () =>
    request(`${API_URL}reporte/cotizaciones/filtro`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getReporteCotizacion = (params) => {
  const key = `getReporteCotizacion-${params}`;
  return deduplicate(key, () =>
    request(`${API_URL}reporte/cotizaciones/tabla?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getReporteNotaVenta = (params) => {
  const key = `getReporteNotaVenta-${params}`;
  return deduplicate(key, () =>
    request(`${API_URL}reporte/notaventas/tabla?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getReporteBloqueados = (params) => {
  const key = `getReporteBloqueados-${params}`;
  return deduplicate(key, () =>
    request(`${API_URL}reporte/bloqueados/tabla?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const actualizarPagadoCotizacion = (id, pagado) => {
  const params = new URLSearchParams();

  params.append("id", id);
  params.append("pagado", pagado);

  return request(`${API_URL}reporte/bloqueados/pagado?${params.toString()}`, {
    method: "PUT",
    headers: getHeaders(),
  });
};

export const getReporteTiempoCotizacion = (params) => {
  const key = `getReporteTiempoCotizacion-${params}`;
  return deduplicate(key, () =>
    request(`${API_URL}reporte/tiempoCotizacion/tabla?${params.toString()}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getReporteDetalleNoConfirmado = (params) => {
  const key = `getReporteDetalleNoConfirmado-${params}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}reporte/detalleNoConfirmado/tabla?${params.toString()}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const getReporteCotizacionHistoria = (id) => {
  const key = `getReporteCotizacionHistoria-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}reporte/cotizaciones/historia?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const recotizarCotizacion = (id) => {
  const key = `recotizarCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/recotizar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getReporteSelectCotizacion = (id) => {
  const key = `getReporteSelectCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}reporte/cotizaciones/cotizacion?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const grabarEstadoCotizacion = (
  id,
  id_detalles,
  id_estado,
  id_estados,
) => {
  const key = `grabarEstadoCotizacion-${id}-${JSON.stringify(
    id_detalles,
  )}-${id_estado}-${JSON.stringify(id_estados)}`;

  const params = new URLSearchParams();

  if (id !== undefined && id !== null) {
    params.append("id", id);
  }

  const appendMulti = (name, value) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null) {
          params.append(name, v);
        }
      });
    } else if (value !== undefined && value !== null) {
      params.append(name, value);
    }
  };

  appendMulti("id_detalles", id_detalles);
  appendMulti("id_estado", id_estado);
  appendMulti("id_estados", id_estados);

  const queryString = params.toString();

  return deduplicate(key, () =>
    request(`${API_URL}reporte/cotizaciones/estado/grabar?${queryString}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const cambiarFechaDecision = (id, fecha) => {
  const key = `cambiarFechaDecision-${id}-${fecha}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}reporte/cotizaciones/cambiaFechaDecision?fecha=${encodeURIComponent(
        fecha,
      )}&id=${id}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const cambiarFechaEntrega = (id, fecha) => {
  const key = `cambiarFechaEntrega-${id}-${fecha}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}reporte/cotizaciones/cambiaFechaEntrega?fecha=${encodeURIComponent(
        fecha,
      )}&id=${id}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const enviarCotizacion = (id) => {
  const key = `enviarCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}reporte/cotizaciones/enviar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getFaseCotizacion = (id) => {
  const key = `getFaseCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}reporte/cotizaciones/fase?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const cambiarEstadosCotizacion = (ids_cotizacion, id_estado) => {
  const key = `cambiarEstadosCotizacion-${ids_cotizacion}-${id_estado}`;

  const params = new URLSearchParams();

  const appendMulti = (name, value) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null) {
          params.append(name, v);
        }
      });
    } else if (value !== undefined && value !== null) {
      params.append(name, value);
    }
  };

  appendMulti("id_cotizacion[]", ids_cotizacion); // La API espera parámetros del tipo id_cotizacion[]=3280&id_cotizacion[]=3278

  const queryString = params.toString();

  return deduplicate(key, () =>
    request(
      `${API_URL}reporte/cotizaciones/cambiaEstados?${queryString}&id_estado=${id_estado}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const revisarCotizacion = (id) => {
  const key = `revisarCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/revisar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getCausaCotizacion = (id, id_causa) => {
  const key = `getCausaCotizacion-${id}-${id_causa}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/causa?id=${id}&id_causa=${id_causa}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getBotonCausaCotizacion = (id) => {
  const key = `getBotonCausaCotizacion-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/causa/boton?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

const descargarReporteExcel = async (
  endpointPath,
  params,
  fallbackFilename,
  errorLabel,
) => {
  const queryString = params?.toString?.() ?? String(params ?? "");
  const endpoint = `${API_URL}${endpointPath}${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await fetch(endpoint, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Error al exportar ${errorLabel}: ${response.status}`);
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("Content-Disposition") || "";
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch?.[1] || fallbackFilename;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const exportarReporteCotizacion = (params) =>
  descargarReporteExcel(
    "reporte/cotizaciones/excel",
    params,
    "cotizaciones.xls",
    "cotizaciones",
  );

export const exportarReporteNotaVenta = (params) =>
  descargarReporteExcel(
    "reporte/notaventas/excel",
    params,
    "notas_venta.xls",
    "notas de venta",
  );

export const exportarReporteBloqueados = (params) =>
  descargarReporteExcel(
    "reporte/bloqueados/excel",
    params,
    "cotizaciones_bloqueadas.xls",
    "cotizaciones bloqueadas",
  );

export const exportarReporteTiempoCotizacion = (params) =>
  descargarReporteExcel(
    "reporte/tiempoCotizacion/excel",
    params,
    "tiempo_cotizacion.xls",
    "tiempo de respuesta",
  );

export const exportarReporteDetalleNoConfirmado = (params) =>
  descargarReporteExcel(
    "reporte/detalleNoConfirmado/excel",
    params,
    "detalle_no_confirmado.xls",
    "detalles no confirmados",
  );

/* =========================
Mantenedor de usuarios
========================= */

export const getUsuariosTabla = () => {
  const key = `getUsuariosTabla`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/usuarios/tabla`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getZonasUsuario = (id) => {
  const key = `getZonasUsuario-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/usuarios/zonas?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getEditarUsuario = (id) => {
  const key = `getEditarUsuario-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/usuarios/editar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getEliminarUsuario = (id) => {
  const key = `getEliminarUsuario-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/usuarios/eliminar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const actualizarZonaUsuario = (id, accion, codigo) => {
  const key = `actualizarZonaUsuario-${id}-${accion}-${codigo}`;
  return deduplicate(key, () =>
    request(
      `${API_URL}mantenedor/usuarios/actualizar/zona?accion=${accion}&codigo=${codigo}&id=${id}`,
      {
        method: "GET",
        headers: getHeaders(),
      },
    ),
  );
};

export const getAgregarUsuario = () => {
  const key = `getAgregarUsuario`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/usuarios/agregar`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const grabarUsuario = (data) => {
  const key = `grabarUsuario-${JSON.stringify(data)}`;

  return deduplicate(key, () => {
    const params = new URLSearchParams();
    if (data?.id != null && data.id !== "")
      params.append("id", String(data.id));
    if (data?.codigo != null && data.codigo !== "")
      params.append("codigo", String(data.codigo));
    if (data?.nombre != null && data.nombre !== "")
      params.append("nombre", data.nombre);
    if (data?.usuario != null && data.usuario !== "")
      params.append("usuario", data.usuario);
    if (data?.contrasena != null && data.contrasena !== "")
      params.append("contrasena", data.contrasena);
    if (data?.email != null && data.email !== "")
      params.append("email", data.email);
    if (data?.telefono != null && data.telefono !== "")
      params.append("telefono", String(data.telefono));
    if (data?.perfil != null) params.append("perfil", String(data.perfil));
    if (data?.almacen != null && data.almacen !== "")
      params.append("almacen", data.almacen);
    if (data?.canal != null && data.canal !== "")
      params.append("canal", data.canal);
    if (data?.recibeEmail != null)
      params.append("recibeEmail", String(data.recibeEmail));
    if (data?.activo != null) params.append("activo", String(data.activo));
    if (data?.alter) params.append("alter", "on");

    const endpoint = `${API_URL}mantenedor/usuarios/grabar?${params.toString()}`;

    return request(endpoint, {
      method: "POST",
      headers: getHeaders(),
    });
  });
};

/* =========================
OBRA
========================= */
export const getObrasTabla = (codigo) => {
  const key = `getObrasTabla-${codigo}`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/obras/tabla?codigo=${codigo}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getEliminarObra = (id) => {
  const key = `getEliminarObra-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/obras/eliminar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getGrabarObra = (data) => {
  const key = `getGrabarObra-${JSON.stringify(data)}`;
  return deduplicate(key, () => {
    const params = new URLSearchParams();
    if (data?.id != null && data.id !== "")
      params.append("id", String(data.id));
    if (data?.codigo != null && data.codigo !== "")
      params.append("codigo", String(data.codigo));
    if (data?.descripcion != null && data.descripcion !== "")
      params.append("descripcion", data.descripcion);

    const endpoint = `${API_URL}mantenedor/obras/grabar?${params.toString()}`;

    return request(endpoint, {
      method: "POST",
      headers: getHeaders(),
    });
  });
};

/* =========================
VALIDEZ
========================= */
export const getValidezTabla = () => {
  const key = `getValidezTabla`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/validez/tabla`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

export const getGrabarValidez = (data) => {
  const key = `getGrabarValidez-${JSON.stringify(data)}`;
  return deduplicate(key, () => {
    const params = new URLSearchParams();
    if (data?.id != null && data.id !== "")
      params.append("id", String(data.id));
    if (data?.descripcion != null && data.descripcion !== "")
      params.append("descripcion", data.descripcion);

    const endpoint = `${API_URL}mantenedor/validez/grabar?${params.toString()}`;

    return request(endpoint, {
      method: "POST",
      headers: getHeaders(),
    });
  });
};

export const getEliminarValidez = (id) => {
  const key = `getEliminarValidez-${id}`;
  return deduplicate(key, () =>
    request(`${API_URL}mantenedor/validez/eliminar?id=${id}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};

/* =========================
mayorista
========================= */
export const enviarCotizacionMayorista = (idCotizacion, destinos, otros) => {
  const key = `enviarCotizacionMayorista-${idCotizacion}-${JSON.stringify(
    destinos,
  )}-${otros}`;

  const params = new URLSearchParams();
  if (idCotizacion != null) params.append("id", String(idCotizacion));

  const appendMulti = (name, value) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null) {
          params.append(name, String(v));
        }
      });
    } else if (value !== undefined && value !== null) {
      params.append(name, String(value));
    }
  };

  appendMulti("destinos[]", destinos);

  if (otros != null && otros !== "") {
    params.append("otros", String(otros));
  }

  const queryString = params.toString();

  return deduplicate(key, () =>
    request(`${API_URL}cotizacion/enviar?${queryString}`, {
      method: "GET",
      headers: getHeaders(),
    }),
  );
};
